import classNames from "classnames";
import findIndex from "lodash/findIndex";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { Typography } from "PFComponents/typography";
import { GroupOption, Option, OptionOriginal } from "PFTypes/options";
import { useRef } from "react";

import { SelectV2Props } from "../../select_v2.types";
import { flattenOptions, getIds } from "../../select_v2.utils";
import { HighlightedIndex } from "../use_highlighted_index";
import css from "./list.module.scss";
import { getListItemId } from "./list.utils";
import { ListItem } from "./list_item";
import { useAutoScrollToSelected } from "./use_auto_scroll_to_selected";

type ListProps<T> = {
  id: string;
  optionsGroups: GroupOption<T>[];
  onChange: (option: Option<T>, selected: boolean) => void;
  highlightedIndex: HighlightedIndex;
  highlightOption: (option: Option<T>, options: Option<T>[]) => void;
  clipOptionsOverflow?: boolean;
} & Pick<SelectV2Props<T>, "value" | "multiple">;

export const ListGrouped = <T extends OptionOriginal = OptionOriginal>({
  id,
  value,
  optionsGroups,
  multiple,
  onChange,
  highlightedIndex,
  highlightOption,
  clipOptionsOverflow
}: ListProps<T>) => {
  const selectedIds = getIds(value);
  const flatOptions = flattenOptions(optionsGroups);
  const listRef = useRef<HTMLUListElement>(null);
  useAutoScrollToSelected({ listRef, multiple, selectedIds, listId: id });

  if (isEmpty(optionsGroups)) {
    return null;
  }

  return (
    <ul
      ref={listRef}
      id={id}
      role="listbox"
      className={css.list}
      aria-multiselectable={multiple}
      tabIndex={-1}
    >
      {(optionsGroups ?? []).map(({ id: groupId, label, options, disabled, selectionLimit }) => (
        <div key={groupId} aria-label="group" className={classNames(css.group, { [css.disabled]: disabled })}>
          {!!label && options.length > 0 && (
            <Typography withMargin variant="labelRegular" tag="span" className={css.groupLabel}>
              {label}
            </Typography>
          )}
          {options.map((option) => {
            const selected = selectedIds.includes(option.id);
            const flattenOptionsIndex = findIndex(flatOptions, ({ id }) => option.id === id);

            const alreadySelectedIdsInGroup = options
              .filter(({ id }) => selectedIds.includes(id))
              .map(({ id }) => id);

            const isGroupSelectionLimitExceeded =
              !isNil(selectionLimit) && selectionLimit <= alreadySelectedIdsInGroup.length;
            const isOptionDisabled =
              isGroupSelectionLimitExceeded &&
              selectionLimit !== 1 &&
              !alreadySelectedIdsInGroup.includes(option.id);
            return (
              <ListItem
                id={getListItemId(id, String(option.id))}
                key={`${id}-${groupId}-${option.id}`}
                option={option}
                selected={selected}
                highlighted={highlightedIndex === flattenOptionsIndex}
                onClick={() => {
                  onChange(option, !selected);
                  highlightOption(option, flatOptions);
                }}
                multiple={multiple && (isNil(selectionLimit) || selectionLimit > 1)}
                clipOptionsOverflow={clipOptionsOverflow}
                disabled={isOptionDisabled}
              />
            );
          })}
        </div>
      ))}
    </ul>
  );
};
